@if (failed) {
  <cs-info-card type="warning" [dismissible]="false">
    {{ LL.bodyCompOverview.loadFailed() }}
    <cs-button tertiary (click)="retry.emit()">{{ LL.bodyCompOverview.retry() }}</cs-button>
  </cs-info-card>
} @else if (!model) {
  <!-- no model yet: a spinner only while a load is really running. A host that has not started
       loading (the check-up before its Start button) draws nothing, so the page does not look busy -->
  @if (loading) {
    <div class="overview-loading">
      <cs-spinner></cs-spinner>
    </div>
  }
} @else {
  <div class="overview-grid" [class.reloading]="loading">

    <!-- 1: progress check -->
    <cs-card class="overview-card progress-check">
      <cs-card-title>{{ LL.bodyCompOverview.progressCheck() }}</cs-card-title>
      <cs-card-content>
        <div class="headline">
          @if (model.progress.status) {
            <span class="status-badge"
              [class.on-track]="model.progress.status === ProgressStatus.OnTrack"
              [class.off-track]="model.progress.status === ProgressStatus.OffTrack"
              [class.undetermined]="model.progress.status === ProgressStatus.Undetermined">
              {{ model.progress.statusText }}
            </span>
          }
          <span class="trend-text">{{ model.progress.trendText }}</span>
        </div>
        @if (model.progress.changeTexts.length > 0) {
          <div class="detail-line">
            @for (change of model.progress.changeTexts; track change; let last = $last) {
              <span>{{ change }}</span>@if (!last) {<span class="separator"> · </span>}
            }
          </div>
        }
        @if (model.progress.footnote) {
          <cs-note>{{ model.progress.footnote }}</cs-note>
        }
      </cs-card-content>
    </cs-card>

    <!-- 2: weight response -->
    <cs-card class="overview-card weight-response">
      <cs-card-title>{{ LL.bodyCompOverview.weightResponse() }}</cs-card-title>
      <cs-card-content>
        @if (model.weightResponse.emptyText) {
          <div class="empty-text">{{ model.weightResponse.emptyText }}</div>
        } @else {
          <div class="headline">
            <span class="big-figure">{{ model.weightResponse.perWeekText }}</span>
            @if (model.weightResponse.percentText) {
              <span class="percent-figure">{{ model.weightResponse.percentText }}</span>
            }
          </div>
        }
        @if (model.weightResponse.atKcalText) {
          <div class="detail-line">
            <span class="at-kcal">{{ model.weightResponse.atKcalText }}</span>
            <span class="separator"> · </span>
            <span>{{ model.weightResponse.intakeSourceText }}</span>
          </div>
        }
      </cs-card-content>
    </cs-card>

    <!-- 3: weight chart -->
    <cs-card class="overview-card weight-chart">
      <cs-card-title>
        {{ LL.common.measurements.weight() }}
        @if (model.weight.nowText) {
          <cs-note>{{ model.weight.nowText }}</cs-note>
        }
      </cs-card-title>
      <cs-card-content>
        @if (model.weight.emptyText) {
          <div class="empty-text">{{ model.weight.emptyText }}</div>
        } @else {
          <cs-trend-band-chart
            [points]="model.weight.chart.points"
            [band]="model.weight.chart.band"
            [firstDayIso]="model.weight.chart.firstDayIso"
            [lastDayIso]="model.weight.chart.lastDayIso"
            [color]="weightColor"
            [unitName]="model.weight.chart.unitName"
            [formatDate]="formatDate">
          </cs-trend-band-chart>
        }
      </cs-card-content>
    </cs-card>

    <!-- 4: energy intake -->
    <cs-card class="overview-card energy-intake">
      <cs-card-title>
        {{ LL.bodyCompOverview.energyIntake() }}
        @if (model.intake?.averageText) {
          <cs-note>{{ model.intake.averageText }}</cs-note>
        }
      </cs-card-title>
      <cs-card-content>
        @if (model.intake; as intake) {
          <div class="intake-chart">
            <div class="bars-area">
              @if (targetLinePercent(intake.targetKcal, intake.maxKcal); as targetPercent) {
                <div class="target-line" [style.bottom.%]="targetPercent">
                  <span class="target-label">{{ intake.targetText }}</span>
                </div>
              }
              @for (day of intake.days; track day.dateIso) {
                <div class="bar-track" [class.unticked]="!day.ticked">
                  <div class="bar" [style.height.%]="barHeightPercent(day, intake.maxKcal)"></div>
                </div>
              }
            </div>
            <div class="labels-row">
              @for (day of intake.days; track day.dateIso) {
                <div class="day-label">{{ day.label }}</div>
              }
            </div>
          </div>
          <cs-note>
            {{ intake.weekText }}@if (intake.daysText) {<span class="separator"> · </span>{{ intake.daysText }}}
          </cs-note>
        } @else {
          <div class="empty-text">{{ model.noCheckupText }}</div>
        }
      </cs-card-content>
    </cs-card>

    <!-- 5: skinfolds -->
    @if (model.skinfolds; as skinfolds) {
      <cs-card class="overview-card skinfolds">
        <cs-card-title>{{ LL.common.measurements.skinfolds() }}</cs-card-title>
        <cs-card-content>
          @for (row of skinfolds.rows; track row.name) {
            <div class="measure-row">
              <div class="measure-name">{{ row.name }}</div>
              <cs-trend-band-chart class="mini"
                [points]="row.chart.points"
                [band]="row.chart.band"
                [firstDayIso]="row.chart.firstDayIso"
                [lastDayIso]="row.chart.lastDayIso"
                [color]="skinfoldColor"
                [unitName]="row.chart.unitName"
                [formatDate]="formatDate">
              </cs-trend-band-chart>
              <div class="measure-latest">{{ row.latestText }}</div>
              <div class="measure-change">{{ row.changeText }}</div>
            </div>
          }
          @if (skinfolds.footnote) {
            <cs-note>{{ skinfolds.footnote }}</cs-note>
          }
        </cs-card-content>
      </cs-card>
    }

    <!-- 6: waist -->
    @if (model.waist; as waist) {
      <cs-card class="overview-card waist">
        <cs-card-title>
          {{ waist.name }}
          @if (waist.latestText) {
            <cs-note>{{ waist.latestText }}</cs-note>
          }
        </cs-card-title>
        <cs-card-content>
          <cs-trend-band-chart
            [points]="waist.chart.points"
            [band]="waist.chart.band"
            [firstDayIso]="waist.chart.firstDayIso"
            [lastDayIso]="waist.chart.lastDayIso"
            [color]="waistColor"
            [unitName]="waist.chart.unitName"
            [formatDate]="formatDate">
          </cs-trend-band-chart>
          @if (waist.changeText) {
            <cs-note>{{ waist.changeText }}</cs-note>
          }
        </cs-card-content>
      </cs-card>
    }

    @if (model.noFatReadingsText) {
      <cs-card class="overview-card no-fat-readings">
        <cs-card-title>{{ LL.common.measurements.skinfolds() }}</cs-card-title>
        <cs-card-content>
          <div class="empty-text">{{ model.noFatReadingsText }}</div>
        </cs-card-content>
      </cs-card>
    }

    <!-- 7: adherence -->
    <cs-card class="overview-card adherence">
      <cs-card-title>{{ LL.bodyCompOverview.adherence() }}</cs-card-title>
      <cs-card-content>
        @if (model.adherence; as adherence) {
          @if (adherence.kcalText) {
            <div class="adherence-line kcal">{{ adherence.kcalText }}</div>
          }
          <div class="adherence-line training">{{ adherence.trainingText }}</div>
        } @else {
          <div class="empty-text">{{ model.noCheckupText }}</div>
        }
      </cs-card-content>
    </cs-card>

  </div>
  @if (hasTrendDirections) {
    <cs-trend-legend class="overview-trend-legend"></cs-trend-legend>
  }
}
